import React from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import _ from 'lodash';
import CryptoJS from 'crypto-js';
import envConfig from './envConfig';
import {
  storageFileProcessesStatus,
} from './utils'

function randomUtility(length) {
  return Math.floor(Math.random() * length);
}

export const randomColor = () => {
  const hex = [1, 2, 3, 4, 5, 6, 7, 8, 9, 'A', 'B', 'C', 'D', 'E', 'F'];
  let hexColor = '#';
  for (let i = 0; i < 6; i++) {
    hexColor += hex[randomUtility(hex.length)];
  }
  return hexColor;
};

export const encryptString = (text) => {
  const iv = CryptoJS.lib.WordArray.random(16);
  const keyWordArray = CryptoJS.enc.Utf8.parse(envConfig.EncryptionKey);
  const encrypted = CryptoJS.AES.encrypt(text, keyWordArray, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  const encryptedData = iv
    .concat(encrypted.ciphertext)
    .toString(CryptoJS.enc.Base64);
  return encryptedData;
};

export const animationTransition = {
  duration: 0.5,
  ease: [0.43, 0.13, 0.23, 0.96],
};

export const fadeInMotion = {
  opacity: 1,
  transition: animationTransition,
};

export const fadeOutMotion = {
  opacity: 0,
  transition: animationTransition,
};

export const toLocalTime = (date) => {
  if (date) {
    return dayjs(date).subtract(4, 'hour');
  }
  return dayjs.utc().subtract(4, 'hour')
};

export function formatNumber(number) {
  if (number) {
    return number.toLocaleString();
  }
  return '';
}

export const percentage = (n, decimalPoints) => {
  let number = n;
  if (number === null || number === undefined) {
    return '0';
  }
  if (number) {
    const wasNegative = number < 0;
    number = parseFloat(number.toString().replace(',', ''));
    if (wasNegative) {
      number = (-1 * number).toFixed(decimalPoints);
    } else {
      number = number.toFixed(decimalPoints);
    }
    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = parts.join('.');
    if (wasNegative) {
      return `(${result}%)`;
    }
    return `${result}%`;
  }
  return '0';
}

export const thousand = (n, decimalPoints) => {
  let number = n;
  if (number === null || number === undefined) {
    return '-';
  }
  if (number) {
    const wasNegative = number < 0;

    let tempDecimal = 0;
    if (decimalPoints) {
      tempDecimal = decimalPoints;
    }
    number = parseFloat(number.toString().replace(',', ''));
    if (!decimalPoints && number % 1 === 0) {
      tempDecimal = 0;
    } else if (!decimalPoints) {
      tempDecimal = 2;
    }
    if (wasNegative) {
      number = (-1 * number).toFixed(tempDecimal);
    } else {
      number = number.toFixed(tempDecimal);
    }
    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = parts.join('.');
    if (wasNegative) {
      return `(${result})`;
    }
    return `${result}`;
  }
  return '0';
};


export const toCurrency = (number) => {
  try {
    if (!number) {
      return '-';
    }
    const wasNegative = number < 0;
    let fixed = 0;
    if (wasNegative) {
      fixed = (-1 * number).toFixed(2);
    } else {
      fixed = number.toFixed(2);
    }
    if (wasNegative) {
      return `($${thousand(fixed, 2)})`;
    }
    return `$${thousand(fixed, 2)}`;
  } catch (error) {
    return '-';
  }
};

export const uuidv4 = () =>
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  )

export const uploadHistoryStatusToString = (status) => {
  if (status === 1) {
    return 'processing'
  }
  if (status === 2) {
    return 'processed'
  }
  if (status === 3) {
    return 'failed'
  }
  return 'pending'
};

export const storageFileProcessesStatusToString = (status) => {
  const filtered = _.filter(storageFileProcessesStatus, (s) => s.id === status)
  return filtered[0].name;
}

export const getDatePresets = (t) => [
  {
    label: t('today'),
    value: () => [dayjs(), dayjs()]
  },
  {
    label: t('yesterday'),
    value: () => [dayjs().add(-1, 'day'), dayjs().add(-1, 'day')]
  },
  {
    label: t('current_week'),
    value: () => [dayjs().startOf('week'), dayjs()]
  },
  {
    label: t('last_week'),
    value: () => [dayjs().add(-1, 'week').startOf('week'), dayjs().add(-1, 'week').endOf('week')]
  },
  {
    label: t('current_month'),
    value: () => [dayjs().startOf('month'), dayjs()]
  },
  {
    label: t('past_month'),
    value: () => [dayjs().add(-1, 'months').startOf('month'), dayjs().add(-1, 'months').endOf('month')]
  },
  {
    label: t('last_2_month'),
    value: () => [dayjs().add(-1, 'month').startOf('month'), dayjs()]
  },
  {
    label: t('last_3_month'),
    value: () => [dayjs().add(-2, 'month').startOf('month'), dayjs()]
  }
];

export const getLibertyReportColumns = (t) => [
  {
    title: t('date'),
    key: 'date',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.date ? dayjs(row.date).format('MMM DD, YYYY hh:mm A') : '-'}</span>
    ),
  },
  {
    title: t('facility'),
    key: 'facility',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.facility}</span>
    ),
  },
  {
    title: t('source'),
    key: 'source',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.source}</span>
    ),
  },
  {
    title: t('load'),
    key: 'loadId',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.loadId}</span>
    ),
  },
  {
    title: t('site'),
    key: 'site',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.site}</span>
    ),
  },
  {
    title: t('mfg_part_number'),
    key: 'mfgPartNumber',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.mfgPartNumber}</span>
    ),
  },
  {
    title: t('description'),
    key: 'productDescription',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.productDescription}</span>
    ),
  },
  {
    title: t('category'),
    key: 'productCategory',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.productCategory}</span>
    ),
  },
  {
    title: t('location'),
    key: 'location',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.location}</span>
    ),
  },
  {
    title: t('qty_in_stock'),
    key: 'qtyInStock',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{thousand(row.qtyInStock)}</span>
    ),
  },
  {
    title: t('serialized'),
    key: 'serialized',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.serialized ? <FontAwesomeIcon className="text-green-600" icon={faCheckCircle} /> : <FontAwesomeIcon className="text-red-600" icon={faTimesCircle} />}</span>
    ),
  },
  {
    title: t('process_area'),
    key: 'processArea',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.processArea}</span>
    ),
  },
  // {
  //   title: t('location_facility'),
  //   key: 'locationFacility',
  //   align: 'center',
  //   className: 'text-sm',
  //   render: (row, object, index) => (
  //       <span>{row.locationFacility}</span>
  //   ),
  // },
];

export const getLibertyExportFields = (t) => (
  {
    date: t('date'),
    facility: t('facility'),
    source: t('source'),
    loadId: t('load'),
    site: t('site'),
    mfgPartNumber: t('mfg_part_number'),
    productDescription: t('description'),
    productCategory: t('category'),
    location: t('location'),
    qtyInStock: t('qty_in_stock'),
    serialized: t('serialized'),
    processArea: t('process_area')
  }
);

export const cleanLibertyDataForExport = (data) => _.map(data, (i) => ({
  date: i.date,
  facility: `"${i.facility}"`,
  source: `"${i.source}"`,
  loadId: `"${i.loadId}"`,
  site: `"${i.site}"`,
  mfgPartNumber: `"${i.mfgPartNumber}"`,
  productDescription: `"${i.productDescription}"`,
  productCategory: `"${i.productCategory}"`,
  location: `"${i.location}"`,
  qtyInStock: `"${i.qtyInStock}"`,
  serialized: i.serialized,
  processArea: `"${i.processArea}"`
}));

export const getReceivingReportColumns = (t) => [
    {
      title: t('job_number'),
      key: 'jobNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.jobNumber}</span>
      ),
    },
    {
      title: t('load'),
      key: 'load',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.load}</span>
      ),
    },
    {
      title: t('po_number'),
      key: 'poNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.poNumber}</span>
      ),
    },
    {
      title: t('pallet_id'),
      key: 'palletId',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.palletId}</span>
      ),
    },
    {
      title: t('part_number'),
      key: 'partNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.partNumber}</span>
      ),
    },
    {
      title: t('part_description'),
      key: 'partDescription',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.partDescription}</span>
      ),
    },
    {
      title: t('quantity'),
      key: 'quantity',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{thousand(row.quantity)}</span>
      ),
    },
    {
      title: t('received_date'),
      key: 'receivedDate',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.receivedDate ? dayjs(row.receivedDate).format('MMM DD, YYYY hh:mm A') : '-'}</span>
      ),
    },
    {
      title: t('location'),
      key: 'location',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.location}</span>
      ),
    },
    {
      title: t('process_area'),
      key: 'processArea',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.processArea}</span>
      ),
    },
    {
      title: t('label'),
      key: 'label',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.label}</span>
      ),
    },
    {
      title: t('current_site'),
      key: 'currentSite',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.currentSite}</span>
      ),
    },
    {
      title: t('manufacturer'),
      key: 'manufacturer',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.manufacturer}</span>
      ),
    },
];

export const getReceivingReportExportFields = (t) => (
  {
    jobNumber: t('job_number'),
    load: t('load'),
    poNumber: t('po_number'),
    palletId: t('pallet_id'),
    partNumber: t('part_number'),
    partDescription: t('part_description'),
    receivedDate: t('received_date'),
    location: t('location'),
    processArea: t('process_area'),
    label: t('label'),
    currentSite: t('current_site'),
    manufacturer: t('manufacturer')
  }
);

export const cleanReceivingReportDataForExport = (data) => _.map(data, (i) => ({
  jobNumber: `"${i.jobNumber}"`,
  load: `"${i.load}"`,
  poNumber: `"${i.poNumber}"`,
  palletId: `"${i.palletId}"`,
  partNumber: `"${i.partNumber}"`,
  partDescription: `"${i.partDescription}"`,
  receivedDate: i.receivedDate,
  location: `"${i.location}"`,
  processArea: `"${i.processArea}"`,
  label: `"${i.label}"`,
  currentSite: `"${i.currentSite}"`,
  manufacturer: `"${i.manufacturer}"`
}));

export const getSourceAssetDetailsReportColumns = (t) => [
  {
      title: t('customer'),
      key: 'customerName',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.customerName}</span>
      ),
    },
    {
      title: t('order_job_number'),
      key: 'orderJobNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.orderJobNumber}</span>
      ),
    },
    {
      title: t('po_number_scan_id'),
      key: 'poNumberScanId',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.poNumberScanId}</span>
      ),
    },
    {
      title: t('serial_number'),
      key: 'serialNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.serialNumber}</span>
      ),
    },
    {
      title: t('mfg_part_number'),
      key: 'mfgPartNumber',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.mfgPartNumber}</span>
      ),
    },
    {
      title: t('description'),
      key: 'shortDesc',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.shortDesc}</span>
      ),
    },
    {
      title: t('quantity'),
      key: 'quantity',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{thousand(row.quantity)}</span>
      ),
    },
    {
      title: t('date_shipped'),
      key: 'dateShipped',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.dateShipped ? dayjs(row.dateShipped).format('MMM DD, YYYY hh:mm A') : '-'}</span>
      ),
    },
    {
      title: t('serialized'),
      key: 'serialized',
      align: 'center',
      className: 'text-sm',
      render: (row, object, index) => (
        <span>{row.isSerialized ? <FontAwesomeIcon className="text-green-600" icon={faCheckCircle} /> : <FontAwesomeIcon className="text-red-600" icon={faTimesCircle} />}</span>
      ),
    },
];

export const getSourceAssetDetailsReportExportFields = (t) => (
  {
    customerName: t('customer'),
    orderJobNumber: t('order_job_number'),
    poNumberScanId: t('po_number_scan_id'),
    serialNumber: t('serial_number'),
    mfgPartNumber: t('mfg_part_number'),
    shortDesc: t('description'),
    dateShipped: t('date_shipped'),
    isSerialized: t('serialized')
  }
);

export const cleanSourceAssetDetailsReportDataForExport = (data) => _.map(data, (i) => ({
  customerName: `"${i.customerName}"`,
  orderJobNumber: `"${i.orderJobNumber}"`,
  poNumberScanId: `"${i.poNumberScanId}"`,
  serialNumber: `"${i.serialNumber}"`,
  mfgPartNumber: `"${i.mfgPartNumber}"`,
  shortDesc: `"${i.shortDesc}"`,
  dateShipped: i.dateShipped,
  isSerialized: i.isSerialized,
}));

export const getSourceStatusReportColumns = (t) => [
  {
    title: '#',
    key: 'orderNumber',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.orderNumber}</span>
    ),
  },
  {
    title: t('status'),
    key: 'orderStatus',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.orderStatus}</span>
    ),
  },
  {
    title: t('items'),
    key: 'itemCount',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{thousand(row.itemCount)}</span>
    ),
  },
  {
    title: t('part_number'),
    key: 'partNumber',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.partNumber}</span>
    ),
  },
  {
    title: t('part_description'),
    key: 'partDescription',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.partDescription}</span>
    ),
  },
  {
    title: t('sales_force_number'),
    key: 'salesforceOrderNumber',
    align: 'center',
    className: 'text-sm',
    render: (row, object, index) => (
      <span>{row.salesforceOrderNumber}</span>
    ),
  },
  {
    title: t('customer'),
    key: 'customer',
    align: 'center',
    className: 'text-sm',
    render: (record, object, index) => (
      <>
        <p>{record.customerName}</p>
        <p>{record.customerPhoneNumber}</p>
        <p>{record.customerEmail}</p>
      </>
    ),
  },
  {
    title: t('shipping'),
    key: 'shipping',
    align: 'center',
    className: 'text-sm',
    render: (record, object, index) => (
      <>
        <p>{record.shippingAddress}</p>
        <p>{record.trackingNumber}</p>
        <p>{record.fedExAPIStatus}</p>
      </>
    ),
  },
  {
    title: t('issues_and_resolutions'),
    key: 'shipping',
    align: 'center',
    className: 'text-sm',
    render: (record, object, index) => (
      <>
        {record.issuesAndResolutions?.length > 0 && (
          <span>{record.issuesAndResolutions}</span>
        )}
        {!record.issuesAndResolutions?.length > 0 && (
          <span className="text-gray-400">N/A</span>
        )}
      </>
    ),
  },
  {
    title: t('creation_date'),
    key: 'orderCreationDate',
    align: 'center',
    className: 'text-sm',
    render: (row) => (
      <span>{row.orderCreationDate ? dayjs(row.orderCreationDate).format('MMM DD, YYYY') : '-'}</span>
    ),
  }
];

export const getSourceStatusReportExportFields = (t) => (
  {
    orderNumber: t('order_number'),
    orderStatus: t('status'),
    itemCount: t('items'),
    partNumber: t('part_number'),
    partDescription: t('part_description'),
    salesforceOrderNumber: t('sales_force_number'),
    customerName: t('customer'),
    customerPhoneNumber: t('customer_phone'),
    customerEmail: t('customer_email'),
    shippingAddress: t('shipping_address'),
    trackingNumber: t('tracking_number'),
    fedExAPIStatus: t('fedEx_api_status'),
    issuesAndResolutions: t('issues_and_resolutions'),
    orderCreationDate: t('creation_date')
  }
);

export const cleanSourceStatusReportDataForExport = (data) => _.map(data, (i) => ({
  orderNumber: `"${i.orderNumber}"`,
  orderStatus: `"${i.orderStatus}"`,
  itemCount: i.itemCount,
  partNumber: `"${i.partNumber}"`,
  partDescription: `"${i.partDescription}"`,
  salesforceOrderNumber: `"${i.salesforceOrderNumber}"`,
  customerName: `"${i.customerName}"`,
  customerPhoneNumber: `"${i.customerPhoneNumber}"`,
  customerEmail: `"${i.customerEmail}"`,
  shippingAddress: `"${i.shippingAddress}"`,
  trackingNumber: `"${i.trackingNumber}"`,
  fedExAPIStatus: `"${i.fedExAPIStatus}"`,
  issuesAndResolutions: `"${i.issuesAndResolutions}"`,
  orderCreationDate: i.orderCreationDate
}));
